/**
 * i18next factory. Single instance per app; the provider imports this once.
 *
 * Design choices:
 *   - Namespaces are CODE-SPLIT per language and per namespace. Loading `ru`
 *     doesn't download `kk`; opening the antifraud section doesn't download
 *     incident-mgr strings. The bundle-per-language cost is bounded to what
 *     the user actually sees.
 *   - The initial page ships the `common` and `shell` namespaces bundled
 *     with the app entry — these strings are needed BEFORE any lazy chunk
 *     loads (nav, header, loading states). Everything else is lazy.
 *   - We do NOT wire an HTTP backend or file-system backend. Locales live in
 *     the bundle. This keeps the console offline-tolerant and removes an
 *     extra 200-800ms of blocking on cold-start.
 *
 * SOLID S: this file owns i18next wiring. Provider owns React glue. Hooks
 * own consumer ergonomics. Nothing else in this package reaches into i18next
 * directly.
 */
import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LANG, FALLBACK, type Lang } from './config.js';

// Statically imported "always available" namespaces. These are the strings
// the shell needs on the very first paint, before any route lazy-loads.
import enCommon from './locales/en/common.json' with { type: 'json' };
import enShell from './locales/en/shell.json' with { type: 'json' };
import ruCommon from './locales/ru/common.json' with { type: 'json' };
import ruShell from './locales/ru/shell.json' with { type: 'json' };
import kkCommon from './locales/kk/common.json' with { type: 'json' };
import kkShell from './locales/kk/shell.json' with { type: 'json' };

let instance: I18nInstance | null = null;

// Every namespace's per-language loader map, stashed at registerNamespace
// time so we can lazy-load missing bundles when the user switches language
// at runtime. Without this, switching to `ru` after the app booted in `en`
// leaves every service namespace (`iam`, `fn`, `gate`, …) with only the
// English bundle loaded — the switch APPEARS to do nothing until a page
// refresh, because useT() falls back to the English resource.
const registered = new Map<
  string,
  Partial<Record<Lang, () => Promise<{ default: Record<string, unknown> }>>>
>();

/** Idempotent — returns the same singleton if called twice, so a stale hot-
 *  reload of the provider doesn't spin up a second i18next behind the app. */
export function getI18n(initialLang: Lang = DEFAULT_LANG): I18nInstance {
  if (instance) return instance;
  instance = i18next.createInstance();
  void instance.use(initReactI18next).init({
    lng: initialLang,
    fallbackLng: FALLBACK[initialLang],
    supportedLngs: ['en', 'ru', 'kk'],
    ns: ['common', 'shell'],
    defaultNS: 'common',
    resources: {
      en: { common: enCommon, shell: enShell },
      ru: { common: ruCommon, shell: ruShell },
      kk: { common: kkCommon, shell: kkShell },
    },
    interpolation: { escapeValue: false }, // React already escapes.
    returnEmptyString: false,
    // Allow the switcher to change language without full-page reload.
    react: { useSuspense: false },
  });

  // Runtime language-switch hydration: any service namespace registered
  // via registerNamespace() gets its bundle for the newly-selected
  // language loaded on demand, then i18next re-emits so useT() re-renders
  // with the fresh strings. Without this the switcher would only work at
  // page reload.
  instance.on('languageChanged', (lng) => {
    const target = lng as Lang;
    void Promise.all(
      Array.from(registered.entries()).map(async ([ns, imports]) => {
        if (instance!.hasResourceBundle(target, ns)) return;
        const load = imports[target];
        if (!load) return;
        try {
          const mod = await load();
          instance!.addResourceBundle(target, ns, mod.default, /* deep */ true, /* overwrite */ false);
        } catch {
          // Missing locale file — non-fatal; falls back to English via i18next.
        }
      }),
    );
  });
  return instance;
}

/** Runtime namespace loader for service packages. Each `service-*` package
 *  calls `registerNamespace('iam', imports)` at import time so its strings
 *  are available before its screens render.
 *
 *  `imports` is a map lang → () => Promise<json>, so we only fetch the
 *  needed language chunk. Consumers pass Vite-friendly dynamic imports.
 *  The map is retained internally — when the user switches language later,
 *  the newly-selected language's bundle is loaded lazily (see getI18n's
 *  languageChanged handler).
 */
export async function registerNamespace(
  ns: string,
  imports: Partial<Record<Lang, () => Promise<{ default: Record<string, unknown> }>>>,
): Promise<void> {
  const i18n = getI18n();
  // Retain the loader map so language switches at runtime can hydrate
  // this namespace lazily.
  registered.set(ns, imports);
  const lang = (i18n.language as Lang) || DEFAULT_LANG;
  // Load current language + English fallback if not already registered.
  const langsToLoad: Lang[] = [lang];
  if (lang !== 'en' && imports.en) langsToLoad.push('en');
  await Promise.all(
    langsToLoad.map(async (l) => {
      const load = imports[l];
      if (!load) return;
      if (i18n.hasResourceBundle(l, ns)) return;
      const mod = await load();
      i18n.addResourceBundle(l, ns, mod.default, /* deep */ true, /* overwrite */ false);
    }),
  );
}

/** Change language at runtime. Persists to localStorage so the next session
 *  boots into the same language before any React render. */
export async function setLanguage(lang: Lang): Promise<void> {
  const i18n = getI18n();
  await i18n.changeLanguage(lang);
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('vex.console.lang', lang);
    }
  } catch {
    // localStorage unavailable (private mode, quota). Non-fatal.
  }
  // Update <html lang> so screen readers announce content correctly.
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
  }
}
